import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Camera, CheckCircle2, FileScan, ImagePlus, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { AffiliateBanner } from "@/components/AffiliateBanner";
import { LocationPicker } from "@/components/LocationPicker";
import { CardVisual } from "@/components/CardVisual";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSeo } from "@/hooks/useSeo";
import { useHref, useLang, useT } from "@/i18n";
import { useCategoryLabel } from "@/i18n/categories";

const empty = {
  business_name: "",
  phone_number: "",
  state: "",
  district: "",
  category: "",
  street_address: "",
  postcode: "",
  description: "",
  owner_email: "",
};

export default function Submit() {
  const navigate = useNavigate();
  const href = useHref();
  const t = useT();
  const lang = useLang();
  const planLabel = (p) =>
    !p ? "" : lang === "en" ? p.label.replace(/bulan/i, p.key === "1m" ? "month" : "months") : p.label;
  const catLabel = useCategoryLabel();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const photoRef = useRef(null);
  const [locations, setLocations] = useState({ states: {}, categories: [] });
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploads, setUploads] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [extraPhoto, setExtraPhoto] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pin, setPin] = useState(null);
  const [addon, setAddon] = useState(() => new URLSearchParams(window.location.search).get("pakej") || "");
  const [coupon, setCoupon] = useState("");
  const [couponOk, setCouponOk] = useState(false);
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [params] = useSearchParams();

  useSeo({
    title: t("submit.seo.title"),
    description: t("submit.seo.desc"),
    canonical: `${window.location.origin}${window.location.pathname}`,
  });

  const addonOptions = useMemo(() => {
    if (!settings) return [];
    const plans = settings.featured_plans || [];
    return [
      { value: "", label: t("addon.basic"), note: t("addon.basic.note"), price_sen: 0 },
      {
        value: "gallery",
        label: t("addon.gallery"),
        note: t("addon.gallery.note", { n: (settings.max_photos || 7) - (settings.gallery_free_photos || 1) }),
        price_sen: settings.gallery_price_sen,
      },      ...plans.map((p) => ({
        value: `featured_${p.key}`,
        label: t("addon.featured", { label: planLabel(p) }),
        note: t("addon.featured.note"),
        price_sen: p.price_sen,
      })),
      ...plans.map((p) => ({
        value: `bundle_${p.key}`,
        label: t("addon.bundle", { label: planLabel(p) }),
        note: t("addon.bundle.note", { saving: p.bundle_saving_label }),
        price_sen: p.bundle_price_sen,
        saving: p.bundle_saving_label,
      })),
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const selectedAddon = addonOptions.find((o) => o.value === addon && o.value);
  const totalSen = couponOk ? 0 : (settings?.price_sen || 0) + (selectedAddon?.price_sen || 0);

  useEffect(() => {
    const wanted = params.get("pakej");
    if (wanted && addonOptions.length && !addonOptions.some((o) => o.value === wanted)) setAddon("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addonOptions]);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/locations").then(({ data }) => setLocations(data));
    api.get("/settings/public").then(({ data }) => setSettings(data));
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value, ...(key === "state" ? { district: "" } : {}) }));

  const MAX_SCANS = 2;

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = "";
    if (!file) return;
    if (uploads.length >= MAX_SCANS) {
      toast.error(t("submit.toast.maxDocs", { max: MAX_SCANS }));
      return;
    }
    setScanning(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/listings/upload-card", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploads((prev) => [...prev, data]);
      const ocr = data.ocr || {};
      const stateOk = Boolean(locations.states?.[ocr.state]);
      const districtOk = stateOk && (locations.states[ocr.state] || []).includes(ocr.district);
      let found = 0;
      setForm((f) => {
        const next = { ...f };
        const fill = (key, value) => {
          if (!value) return;
          found += 1;
          if (!next[key]) next[key] = value;
        };
        fill("business_name", ocr.business_name);
        fill("phone_number", ocr.phone_number);
        fill("owner_email", ocr.owner_email);
        fill("street_address", ocr.street_address);
        fill("postcode", ocr.postcode);
        fill("category", locations.categories?.includes(ocr.category) ? ocr.category : "");
        if (stateOk) {
          fill("state", ocr.state);
          if (districtOk) fill("district", ocr.district);
        }
        return next;
      });
      if (ocr.error) {
        toast.error(t("submit.toast.ocrFail"));
      } else if (found > 0) {
        toast.success(t("submit.toast.ocrFound", { n: found }));
      } else {
        toast.info(t("submit.toast.ocrNone"));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setScanning(false);
    }
  };

  const handleExtraPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t("submit.toast.photoSize"));
      if (photoRef.current) photoRef.current.value = "";
      return;
    }
    setPhotoUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/listings/upload-photo", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExtraPhoto(data);
      toast.success(t("submit.toast.photoUploaded"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPhotoUploading(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const submit = async (event) => {    event.preventDefault();
    if (!form.business_name || !form.phone_number || !form.state || !form.district || !form.category) {
      toast.error(t("submit.toast.required"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/listings", {
        ...form,
        storage_path: uploads[0]?.storage_path || null,
        extra_photo_path: extraPhoto?.storage_path || null,
        search_tags: [...new Set(uploads.flatMap((u) => u.ocr?.search_tags || []))],
        ocr_raw_text: uploads.map((u) => u.ocr?.raw_text).filter(Boolean).join("\n---\n") || null,
        lat: pin?.lat ?? null,
        lng: pin?.lng ?? null,
        addon: addon || null,
        coupon: couponOk ? coupon : null,
      });
      if (data.payment_url) {
        if (data.resume_url) localStorage.setItem("dirbiz_resume_url", data.resume_url);
        window.location.assign(data.payment_url);
        return;
      }
      setDone(data);
      toast.success(t("submit.toast.live"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const districts = form.state ? locations.states?.[form.state] || [] : [];
  const tags = [...new Set(uploads.flatMap((u) => u.ocr?.search_tags || []))];

  if (done) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 sm:px-8">
        <div data-testid="submission-success" className="rounded-lg border border-emerald-200 bg-emerald-50 p-10 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
          <h1 className="mt-5 font-display text-2xl font-bold tracking-tight">{t("submit.done.title")}</h1>
          <p className="mt-3 text-sm leading-relaxed text-emerald-900">
            {t("submit.done.body", { status: done.status === "approved" ? "LIVE (Approved)" : done.status })}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button data-testid="view-directory-button" onClick={() => navigate(href("/directory"))} className="bg-primary hover:bg-[#0052CC]">
              {t("submit.done.viewDir")}
            </Button>
            <Button variant="outline" data-testid="submit-another-button" onClick={() => { setDone(null); setForm(empty); setUploads([]); }}>
              {t("submit.done.again")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AffiliateBanner url={settings?.affiliate_url} testId="affiliate-banner-submit" />

      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("submit.eyebrow")}</p>
        <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
          {t("submit.title")}
        </h1>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
          {t("submit.intro")}
        </p>

        {settings && (
          <div
            data-testid="paywall-notice"
            className={`mt-6 rounded-lg border p-4 text-sm ${
              settings.paywall_enabled ? "border-primary/30 bg-accent text-accent-foreground" : "border-emerald-200 bg-emerald-50 text-emerald-900"
            }`}
          >
            {settings.paywall_enabled
              ? t("submit.paywall.on", { price: settings.price_label })
              : t("submit.paywall.off")}
          </div>
        )}

        <form onSubmit={submit} className="mt-10 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">
              {t("submit.docs.label")}
            </Label>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
              {t("submit.docs.hint")}
            </p>
            <div className="mt-3 overflow-hidden rounded-lg border-2 border-dashed border-primary/30 bg-accent/40">
              {uploads.length > 0 ? (
                <div className="grid grid-cols-2 gap-1 bg-white p-1" data-testid="scan-previews">
                  {uploads.map((u, i) => (
                    <div key={u.storage_path} className="relative overflow-hidden rounded">
                      <img src={mediaUrl(u.image_url)} alt={t("submit.docs.alt", { n: i + 1 })} className="aspect-[4/3] w-full object-cover" />
                      <button
                        type="button"
                        data-testid={`scan-remove-${i}`}
                        onClick={() => setUploads((prev) => prev.filter((_, idx) => idx !== i))}
                        className="absolute right-1 top-1 rounded bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-semibold text-white transition-colors duration-200 hover:bg-slate-900"
                      >
                        {t("submit.docs.remove")}
                      </button>
                    </div>
                  ))}
                  {scanning && (
                    <div className="grid aspect-[4/3] place-items-center rounded bg-slate-50 text-primary">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid aspect-[16/10] place-items-center bg-slate-50 text-center">
                  {scanning ? (
                    <div className="flex flex-col items-center gap-2 text-primary">
                      <Loader2 className="h-7 w-7 animate-spin" />
                      <span className="text-sm font-medium">{t("submit.docs.reading")}</span>
                    </div>
                  ) : (
                    <div className="px-6 text-slate-500">
                      <FileScan className="mx-auto h-8 w-8 text-primary" />
                      <p className="mt-2 text-sm">{t("submit.docs.formats")}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFile} className="hidden" data-testid="card-file-input" />
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFile}
              className="hidden"
              data-testid="card-camera-input"
            />
            <Button
              type="button"
              data-testid="scan-camera-button"
              disabled={scanning || uploads.length >= MAX_SCANS}
              onClick={() => cameraRef.current?.click()}
              className="mt-3 h-12 w-full bg-slate-900 text-base font-semibold text-white shadow-sm transition-transform duration-200 hover:-translate-y-0.5 hover:bg-slate-800 sm:hidden"
            >
              {scanning ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Camera className="mr-2 h-5 w-5" />}
              {scanning ? t("submit.docs.readingShort") : t("submit.docs.camera")}
            </Button>
            <Button
              type="button"
              data-testid="upload-card-button"
              disabled={scanning || uploads.length >= MAX_SCANS}
              onClick={() => fileRef.current?.click()}
              className="mt-3 h-12 w-full bg-primary text-base font-semibold text-primary-foreground shadow-sm transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#0052CC] sm:mt-3"
            >
              {scanning ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <FileScan className="mr-2 h-5 w-5" />
              )}
              {scanning
                ? t("submit.docs.readingShort")
                : uploads.length === 0
                  ? t("submit.docs.pick")
                  : uploads.length < MAX_SCANS
                    ? t("submit.docs.second", { n: uploads.length, max: MAX_SCANS })
                    : t("submit.docs.limit", { max: MAX_SCANS })}
            </Button>
            <p className="mt-2 text-xs text-slate-500 sm:hidden">
              {t("submit.docs.cameraNote")}
            </p>
            <p className="mt-2 text-xs text-slate-500">
              {t("submit.docs.privacyNote")}
            </p>

            {tags.length > 0 && (
              <div className="mt-5" data-testid="ocr-tags">
                <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-slate-500">
                  <Sparkles className="h-3.5 w-3.5 text-primary" /> {t("submit.docs.aiTags")}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <Badge key={t} variant="secondary" className="text-[11px]">
                      {t}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid gap-5 lg:col-span-7">
            <div>
              <Label htmlFor="business_name">{t("submit.field.name")}</Label>
              <Input id="business_name" data-testid="business-name-input" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className="mt-2 h-11" />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="phone_number">{t("submit.field.phone")}</Label>
                <Input id="phone_number" data-testid="phone-input" placeholder="012-345 6789" value={form.phone_number} onChange={(e) => set("phone_number", e.target.value)} className="mt-2 h-11" />
              </div>
              <div>
                <Label htmlFor="owner_email">{t("submit.field.email")}</Label>
                <Input id="owner_email" type="email" data-testid="email-input" value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)} className="mt-2 h-11" />
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label>{t("submit.field.state")}</Label>
                <Select value={form.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger className="mt-2 h-11" data-testid="submit-state-select">
                    <SelectValue placeholder={t("submit.select.state")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {Object.keys(locations.states || {}).map((s) => (
                      <SelectItem key={s} value={s} data-testid={`submit-state-option-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("submit.field.district")}</Label>
                <Select value={form.district} onValueChange={(v) => set("district", v)} disabled={!form.state}>
                  <SelectTrigger className="mt-2 h-11" data-testid="submit-district-select">
                    <SelectValue placeholder={t("submit.select.district")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {districts.map((d) => (
                      <SelectItem key={d} value={d} data-testid={`submit-district-option-${d}`}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>{t("submit.field.category")}</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-2 h-11" data-testid="submit-category-select">
                  <SelectValue placeholder={t("submit.select.category")} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(locations.categories || []).map((c) => (
                    <SelectItem key={c} value={c} data-testid={`submit-category-option-${c}`}>{catLabel(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div
              className="rounded-xl border border-border bg-slate-50 p-5"
              data-testid="card-preview-block"
            >
              <p className="text-xs uppercase tracking-[0.15em] text-slate-500">{t("submit.preview.title")}</p>
              <p className="mt-2 text-sm text-slate-600">{t("submit.preview.body")}</p>
              <div className="mt-4 max-w-xs overflow-hidden rounded-lg border border-border shadow-sm">
                <div className="aspect-[1.6/1]">
                  <CardVisual
                    listing={{
                      id: "preview",
                      business_name: form.business_name || t("submit.preview.placeholder"),
                      category: form.category,
                      phone_number: form.phone_number || "01X-XXX XXXX",
                      district: form.district || "—",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Label htmlFor="street_address">{t("submit.field.address")}</Label>
                <Input
                  id="street_address"
                  data-testid="street-address-input"
                  placeholder="No 12, Jalan Besar"
                  value={form.street_address}
                  onChange={(e) => set("street_address", e.target.value)}
                  className="mt-2 h-11"
                />
              </div>
              <div>
                <Label htmlFor="postcode">{t("submit.field.postcode")}</Label>
                <Input
                  id="postcode"
                  data-testid="postcode-input"
                  inputMode="numeric"
                  placeholder="06000"
                  value={form.postcode}
                  onChange={(e) => set("postcode", e.target.value.replace(/\D/g, "").slice(0, 5))}
                  className="mt-2 h-11"
                />
              </div>
            </div>
            <p className="-mt-2 text-xs text-slate-500">
              {t("submit.postcodeNote")}
            </p>

            <div data-testid="location-field">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">{t("submit.map.label")}</Label>
              <LocationPicker
                address={form.street_address}
                postcode={form.postcode}
                district={form.district}
                state={form.state}
                value={pin}
                onChange={setPin}
              />
            </div>

            <div>
              <Label htmlFor="description">{t("submit.field.description")}</Label>
              <Textarea id="description" data-testid="description-input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-2" />
            </div>

            <div data-testid="extra-photo-field">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">
                {t("submit.photo.label")}
              </Label>
              <p className="mt-1.5 text-xs text-slate-500">
                {t("submit.photo.hint")}
              </p>
              <div className="mt-3 overflow-hidden rounded-lg border border-dashed border-border bg-slate-50">
                {extraPhoto ? (
                  <img
                    src={mediaUrl(extraPhoto.image_url)}
                    alt={t("submit.photo.alt")}
                    className="aspect-[16/9] w-full object-cover"
                  />
                ) : (
                  <div className="grid aspect-[16/9] place-items-center text-center text-slate-500">
                    {photoUploading ? (
                      <div className="flex flex-col items-center gap-2 text-primary">
                        <Loader2 className="h-6 w-6 animate-spin" />
                        <span className="text-sm font-medium">{t("submit.photo.uploading")}</span>
                      </div>
                    ) : (
                      <div className="px-6">
                        <ImagePlus className="mx-auto h-6 w-6" />
                        <p className="mt-2 text-sm">{t("submit.docs.formats")}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <input
                ref={photoRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleExtraPhoto}
                className="hidden"
                data-testid="extra-photo-input"
              />
              <Button
                type="button"
                variant="outline"
                disabled={photoUploading}
                data-testid="extra-photo-button"
                onClick={() => photoRef.current?.click()}
                className="mt-3 w-full sm:w-fit"
              >
                {extraPhoto ? t("submit.photo.change") : t("submit.photo.pick")}
              </Button>
            </div>

            <div data-testid="addon-picker" className="rounded-lg border border-border bg-card p-5">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">
                {t("submit.addon.label")}
              </Label>
              <p className="mt-1.5 text-xs text-slate-500">
                {t("submit.addon.hint")}
              </p>
              <div className="mt-4 grid gap-2.5">
                {addonOptions.map((o) => (                  <button
                    key={o.value}
                    type="button"
                    data-testid={`addon-option-${o.value || "none"}`}
                    onClick={() => {
                      setAddon(o.value);
                      if (o.value && couponOk) {
                        setCouponOk(false);
                        toast.info(t("submit.toast.couponRemoved"));
                      }
                    }}
                    className={`flex items-start justify-between gap-3 rounded-lg border p-3.5 text-left transition-colors duration-200 ${
                      addon === o.value ? "border-primary bg-accent/60" : "border-border hover:border-primary/40"
                    }`}
                  >
                    <span>
                      <span className="block text-sm font-semibold text-slate-900">{o.label}</span>
                      {o.note && <span className="mt-0.5 block text-xs text-slate-500">{o.note}</span>}
                    </span>
                    <span className="whitespace-nowrap text-sm font-bold text-slate-900">
                      {o.value ? `+RM${(o.price_sen / 100).toFixed(0)}` : settings?.price_label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div data-testid="coupon-field" className="rounded-lg border border-border bg-card p-5">
              <Label htmlFor="coupon-input" className="text-xs uppercase tracking-[0.15em] text-slate-500">
                {t("submit.coupon.label")}
              </Label>
              <p className="mt-1.5 text-xs text-slate-500">
                {t("submit.coupon.hint")}
              </p>
              <div className="mt-3 flex gap-2">
                <Input
                  id="coupon-input"
                  data-testid="coupon-input"
                  placeholder={t("submit.coupon.placeholder")}
                  value={coupon}
                  onChange={(e) => {
                    setCoupon(e.target.value.toUpperCase());
                    setCouponOk(false);
                  }}
                  className="h-11"
                />
                <Button
                  type="button"
                  variant="outline"
                  data-testid="coupon-apply-button"
                  disabled={!coupon || checkingCoupon}
                  onClick={async () => {
                    setCheckingCoupon(true);
                    try {
                      await api.post("/coupons/validate", { code: coupon });
                      setCouponOk(true);
                      setAddon("");
                      toast.success(t("submit.toast.couponOk"));
                    } catch (err) {
                      setCouponOk(false);
                      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                    } finally {
                      setCheckingCoupon(false);
                    }
                  }}
                  className="h-11 shrink-0"
                >
                  {checkingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : t("submit.coupon.apply")}
                </Button>
              </div>
              {couponOk && (
                <p data-testid="coupon-applied" className="mt-2 text-xs font-semibold text-emerald-700">
                  {t("submit.coupon.applied", { code: coupon })}
                </p>
              )}
            </div>

            <div data-testid="payment-summary" className="rounded-lg border border-primary/30 bg-accent/40 p-5">
              <div className="flex items-center justify-between text-sm text-slate-700">
                <span>{t("submit.summary.base")}</span>
                <span className={`font-semibold ${couponOk ? "line-through text-slate-400" : ""}`}>
                  {settings?.price_label}
                </span>
              </div>
              {selectedAddon && (
                <div className="mt-2 flex items-center justify-between text-sm text-slate-700">
                  <span>{selectedAddon.label}</span>
                  <span className="font-semibold">RM{(selectedAddon.price_sen / 100).toFixed(0)}</span>
                </div>
              )}
              <div className="mt-3 flex items-center justify-between border-t border-primary/20 pt-3">
                <span className="text-sm font-semibold text-slate-900">{t("submit.summary.total")}</span>
                <span data-testid="payment-total" className="font-display text-2xl font-extrabold tracking-tight">
                  RM{(totalSen / 100).toFixed(0)}
                </span>
              </div>
              {selectedAddon?.saving && (
                <p data-testid="payment-saving" className="mt-2 text-xs font-semibold text-emerald-700">
                  {t("submit.summary.saving", { saving: selectedAddon.saving })}
                </p>
              )}
              <p className="mt-2 text-xs text-slate-500">
                {t("submit.summary.note")}
              </p>
            </div>

            <Button type="submit" data-testid="submit-listing-button" disabled={saving} className="h-12 bg-primary text-primary-foreground transition-colors duration-200 hover:bg-[#0052CC]">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {totalSen > 0 ? t("submit.cta.pay", { amount: (totalSen / 100).toFixed(0) }) : t("submit.cta.free")}            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
